import React, { useRef } from 'react';
import type { Reducer } from 'react';
import { Drawer, useStyles } from '@archipro-design/aria';
import DesignBoardDrawerEditMainContent from '../right-side-drawer/DesignBoardDrawerEditMainContent';
import DesignBoardDrawerMovePinsMain from '../right-side-drawer/DesignBoardDrawerMovePinsMain';
import DesignBoardDrawerCopyPinsMain from '../right-side-drawer/DesignBoardDrawerCopyPinsMain';
import type {
    DesignBoardAction,
    DesignBoardDispatch,
    DesignBoardPageContextProviderProps,
    DrawerReducerState,
} from '~/modules/design-board/type/main-context-types';
import { MobileBottomMenuContent } from '~/modules/design-board/component/mobile-menu/MobileBottomMenuContent';
import { DrawerTopWrapper } from '~/modules/design-board/component/page-layout/PageLayout.style';
import { useScrollLock } from '@archipro-design/aria';
import MoodBoardDrawerEditMainContent from '@modules/build-hub/components/build-hub-design-boards/details-view/MoodBoardDrawerEditMainContent';

const DesignBoardPageContext = React.createContext<
    | {
          state: DrawerReducerState;
          dispatch: DesignBoardDispatch;
      }
    | undefined
>(undefined);

const DesignBoardReducer: Reducer<DrawerReducerState, DesignBoardAction> = (
    state,
    action
) => {
    switch (action.type) {
        case 'OpenCreateNewDrawer':
            return {
                ...state,
                designBoardInput: undefined,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'NoPins',
                drawerChild: <DesignBoardDrawerEditMainContent />,
                anchor: 'right',
            };
        case 'OpenEditDrawer':
            return {
                ...state,
                designBoardInput: action.designBoardInput,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'NoPins',
                drawerChild: <DesignBoardDrawerEditMainContent />,
                anchor: 'right',
            };
        case 'OpenCreateMoodBoardDrawer':
            return {
                ...state,
                createMoodBoardInput: action.createMoodBoardInput,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'NoPins',
                drawerChild: <MoodBoardDrawerEditMainContent />,
                anchor: 'right',
            };
        case 'OpenMoodBoardEditDrawer':
            return {
                ...state,
                editMoodBoardInput: action.editMoodBoardInput,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'NoPins',
                drawerChild: <MoodBoardDrawerEditMainContent />,
                anchor: 'right',
            };
        case 'CloseDrawer':
            return { ...state, isDrawerOpen: false, bottomMenuData: undefined };
        case 'SetCurrentBoardPin':
            return {
                ...state,
                currentBoardData:
                    action.currentBoardData || state.currentBoardData,
                currentPinData: action.currentPinData || state.currentPinData,
            };
        case 'UploadPin':
            return {
                ...state,
                isDrawerOpen: false,
                uploadFiles: action.uploadFiles,
                anchor: 'right',
            };
        case 'OpenMovePinsDrawer':
            return {
                ...state,
                isDrawerOpen: true,
                drawerChild: <DesignBoardDrawerMovePinsMain />,
                pinMoveData: action.pinMoveData,
                anchor: 'right',
                bottomMenuData: undefined,
            };
        case 'OpenCopyPinsDrawer':
            return {
                ...state,
                isDrawerOpen: true,
                drawerChild: <DesignBoardDrawerCopyPinsMain />,
                pinMoveData: action.pinMoveData,
                anchor: 'right',
                bottomMenuData: undefined,
            };

        case 'MoveDesignBoardPinsCreateNewDrawer':
            return {
                ...state,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'MovePins',
                drawerChild: <DesignBoardDrawerEditMainContent />,
                pinMoveData: action.pinMoveData || state.pinMoveData,
                anchor: 'right',
            };

        case 'CopyDesignBoardPinsCreateNewDrawer':
            return {
                ...state,
                isDrawerOpen: true,
                CreateBoardFromPinActionType: 'CopyPins',
                drawerChild: <DesignBoardDrawerEditMainContent />,
                pinMoveData: action.pinMoveData || state.pinMoveData,
                anchor: 'right',
            };
        case 'OpenMobileBottomMenuDrawer':
            return {
                ...state,
                isDrawerOpen: true,
                anchor: 'bottom',
                drawerChild: <MobileBottomMenuContent />,
                bottomMenuData: action.bottomMenuData,
                toastMessage: undefined,
            };
        case 'OpenShareBoardModal':
            return { ...state, isShareBoardModalOpen: true };
        case 'CloseShareBoardModal':
            return { ...state, isShareBoardModalOpen: false };
        case 'SetEditMode':
            return {
                ...state,
                canEdit: action.canEdit,
            };
        case 'SetCommentPermission': {
            return {
                ...state,
                canComment: action.canComment,
            };
        }
        default:
            return state;
    }
};

export const DesignBoardPageContextProvider = ({
    children,
}: DesignBoardPageContextProviderProps) => {
    const [state, dispatch] = React.useReducer(DesignBoardReducer, {
        uploadFiles: [],
        isDrawerOpen: false,
        isShareBoardModalOpen: false,
        pinMoveData: undefined,
        drawerChild: <DesignBoardDrawerEditMainContent />,
        CreateBoardFromPinActionType: 'NoPins',
        anchor: 'right',
    });

    const { css } = useStyles({ isSwipeableEdge: !!state.bottomMenuData });

    const scrollRef = useRef<HTMLElement>(null);

    const value = { state, dispatch };

    useScrollLock(scrollRef, state.isDrawerOpen);

    return (
        <DesignBoardPageContext.Provider value={value}>
            {children}

            <Drawer
                onOpenChange={() => {
                    dispatch({ type: 'CloseDrawer' });
                }}
                open={state.isDrawerOpen}
                fluid={true}
                anchor={state.anchor}
                content={state.drawerChild}
                contentRef={scrollRef}
                swipeableEdge={
                    state.bottomMenuData
                        ? {
                              onEdgeSwipe: (e, dir) => {
                                  dir === 'down' &&
                                      dispatch({ type: 'CloseDrawer' });
                              },
                          }
                        : undefined
                }
                className={css(DrawerTopWrapper)}
            />
        </DesignBoardPageContext.Provider>
    );
};

export const useDesignBoardPageState = () => {
    const context = React.useContext(DesignBoardPageContext);

    if (!context) {
        throw new Error('Cannot be used outside DesignBoardPageContext');
    }
    return context;
};
